/* GrowthOS — Opportunity record page.
   Re-skinned to mirror the Lead/Contact/Account detail (`.gos-leadrec` shell): a
   back row, header card (initials avatar, name, Mail button, dots menu, chips), a
   horizontal tab bar and a read-only Overview + sticky Details panel. Tabs reuse
   the SHARED child components (Timeline, Notes, Attachments, Email/compose) — same
   as the other three modules. Tags use the real Opportunity tag APIs (the legacy
   page already dispatched fetchTagsAndAppendToModule/fetchEmailAndAppendToModule
   for 'Opportunity' but never rendered a Tags or Emails UI — both are wired up here
   for the first time, using the exact same generic module-parameterized APIs Lead/
   Contact/Account already use, not a new capability).
   No Call/WhatsApp buttons (Opportunity has no phone field) and no enrichment/report
   menu items (no such API exists for Opportunity, unlike Account) — the dots menu is
   just "Edit Opportunity". No Campaigns/Opportunities/Contacts tabs either: a single
   opportunity doesn't enroll in campaigns as a list (just the one `campaignSource`
   field, already shown in Overview) and obviously can't embed itself. */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchAttachmentsAndAppendToModule,
  fetchDataAndUpdateStore,
  fetchDetailAndAppendToModule,
  fetchEmailAndAppendToModule,
  fetchNotesAndAppendToModule,
  fetchTagsAndAppendToModule,
} from '../../redux/actions';
import { getOpportunityById, getTagsByModule } from '../../redux/selector';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { formatUSCurrency } from '../../shared/constants';
import { DateFormatter } from '../../shared/controls/formatters';
import Loader from '../../shared/component/Loader';
import Notes from '../sharedChild/note/notes';
import Attachments from '../sharedChild/attachment/attachments';
import Email from '../sharedChild/email/emails';
import ComposeEmail from '../sharedChild/email/compose';
import Timeline from '../sharedChild/timeline';
import { Ic } from "../../shared/component/GosIcon";

/* ---------- GrowthOS record icons (stroke, 24×24) ---------- */

const GOS_AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const gosInitials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const gosAvaColor = (name) =>
  GOS_AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % GOS_AVA_COLORS.length];

const val = (v) => (v === 0 || v ? v : null);

const InfoCard = ({ title, icon, rows }) => (
  <div className="gos-lr-card">
    <div className="gos-lr-card-head">
      <div className="gos-lr-card-title">
        <Ic name={icon} size={16} /> {title}
      </div>
    </div>
    <div className="gos-lr-card-rows">
      {rows.map(([label, value], i) => (
        <div className="gos-lr-drow" key={i}>
          <div className="gos-lr-dlabel">{label}</div>
          <div className={'gos-lr-dval' + (val(value) ? '' : ' muted')}>{val(value) ?? '—'}</div>
        </div>
      ))}
    </div>
  </div>
);
const DetailRow = ({ icon, label, children }) => (
  <div className="gos-lr-drow">
    <div className="gos-lr-dlabel">
      <Ic name={icon} size={15} /> {label}
    </div>
    <div className="gos-lr-dval gos-lr-dval-right">{children ?? '—'}</div>
  </div>
);

/* ---------- Opportunity tags editor (add / remove / create) ---------- */
const TAG_TONES = ['blue', 'green', 'amber', 'purple', 'rose', 'gray', 'red'];
const toneFor = (name) =>
  TAG_TONES[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_TONES.length];
const tagName = (t) => (Array.isArray(t?.name) ? t.name.join(', ') : t?.name || '');

const OpportunityTagEditor = ({ opportunity }) => {
  const dispatch = useDispatch();
  const library = useSelector(getTagsByModule('opportunityList')) || [];
  const applied = opportunity?.tags || [];
  const moduleId = opportunity?.id;

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Opportunity', storeKey: 'opportunityList' }));
  }, [dispatch]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQ('');
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open]);

  const refreshTags = () => {
    dispatch(fetchTagsAndAppendToModule({ moduleName: 'Opportunity', id: moduleId }));
    dispatch(fetchDataAndUpdateStore({ key: 'tagsByModule', inputData: 'Opportunity', storeKey: 'opportunityList' }));
  };

  const addTagByName = async (name) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.createTags({ tagName: [name], module: 'Opportunity', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag added successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      toast.error('Failed to add tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const removeTag = async (tag) => {
    if (!moduleId || busy) return;
    setBusy(true);
    try {
      const res = await apiServices.deleteTagsByModuleId({ id: [tag.id], module: 'Opportunity', moduleId: [moduleId] });
      if (res.status === 200) {
        toast.success('Tag removed successfully!', { position: 'top-right' });
        refreshTags();
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      toast.error('Failed to remove tag.', { position: 'top-right' });
    } finally {
      setBusy(false);
    }
  };

  const toggleLibraryTag = (libTag) => {
    const name = tagName(libTag);
    const existing = applied.find((a) => tagName(a) === name);
    if (existing) removeTag(existing);
    else addTagByName(name);
  };

  const ql = q.trim();
  const filtered = ql ? library.filter((t) => tagName(t).toLowerCase().includes(ql.toLowerCase())) : library;
  const exact = library.some((t) => tagName(t).toLowerCase() === ql.toLowerCase());
  const nextTone = TAG_TONES[library.length % TAG_TONES.length];

  const create = () => {
    if (!ql || exact || busy) return;
    addTagByName(ql);
    setQ('');
    inputRef.current?.focus();
  };

  return (
    <div className="gos-lead-tags">
      <span className="gos-lead-tags-label">
        <Ic name="tag" size={13} /> Tags
      </span>
      {applied.map((t) => {
        const tone = toneFor(tagName(t));
        return (
          <span
            key={t.id}
            className="gos-lead-tag"
            style={{ background: `var(--gos-${tone}-bg)`, color: `var(--gos-${tone}-fg)` }}
          >
            <span className="gos-lt-dot" />
            {tagName(t)}
            <button type="button" className="gos-lt-x" title="Remove tag" onClick={() => removeTag(t)} disabled={busy}>
              <Ic name="x" size={13} />
            </button>
          </span>
        );
      })}
      <div className="gos-tag-add-wrap" ref={wrapRef}>
        <button type="button" className={'gos-tag-add' + (open ? ' on' : '')} onClick={() => setOpen((o) => !o)}>
          <Ic name="plus" size={14} /> Add tag
        </button>
        {open ? (
          <div className="gos-tag-pop">
            <div className="gos-tag-pop-search">
              <Ic name="search" size={14} />
              <input
                ref={inputRef}
                placeholder="Search or create…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && ql && !exact) create();
                }}
              />
            </div>
            <div className="gos-tag-pop-label">{ql ? 'Matching tags' : 'Apply tags'}</div>
            <div className="gos-tag-pop-list">
              {filtered.length === 0 && !ql ? <div className="gos-tag-pop-empty">No tags yet</div> : null}
              {filtered.map((t) => {
                const name = tagName(t);
                const on = applied.some((a) => tagName(a) === name);
                const tone = toneFor(name);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={'gos-tag-opt' + (on ? ' on' : '')}
                    onClick={() => toggleLibraryTag(t)}
                    disabled={busy}
                  >
                    <span className="gos-to-dot" style={{ background: `var(--gos-${tone}-fg)` }} />
                    {name}
                    <span className="gos-to-check">{on ? <Ic name="check" size={12} /> : null}</span>
                  </button>
                );
              })}
              {ql && filtered.length === 0 ? (
                <div className="gos-tag-pop-empty">No matches — create it below</div>
              ) : null}
            </div>
            {ql && !exact ? (
              <button type="button" className="gos-tag-create" onClick={create} disabled={busy}>
                <span
                  className="gos-tc-swatch"
                  style={{ background: `var(--gos-${nextTone}-bg)`, border: `1px solid var(--gos-${nextTone}-fg)` }}
                />
                Create <strong>{`“${ql}”`}</strong>
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ViewDeal = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [menuOpen, setMenuOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const stateData = useSelector((state) => state);
  const storeLoading = isLoadingByKey(stateData, 'opportunityList');
  const [loading, setLoading] = useState(storeLoading);

  const searchParams = new URLSearchParams(location.search);
  const opportunityID = searchParams.get('id');
  const [opportunityLocalData, setOpportunityLocalData] = useState(location.state || {});
  const opportunityStoreData = useSelector((state) =>
    getOpportunityById(state, location.state?.id || opportunityID)
  );

  useEffect(() => {
    setOpportunityLocalData((prev) => ({ ...prev, ...opportunityStoreData }));
  }, [opportunityStoreData]);

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: selectedOption }));
  };

  const handleOpenEditForm = () => {
    navigate('/opportunity/editOpportunity', { state: { ...opportunityLocalData, type: 'view', from: 'view' } });
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    const opportunityId = location.state?.id || opportunityID;
    if (opportunityId) {
      dispatch(fetchDetailAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId, localDetail: location.state || null }));
      dispatch(fetchNotesAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchAttachmentsAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchEmailAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
      dispatch(fetchTagsAndAppendToModule({ moduleName: 'Opportunity', id: opportunityId }));
    }
  }, [location, opportunityLocalData?.id]);

  const od = opportunityLocalData || {};
  const name = od.name || 'Opportunity';
  const amount = od.amount != null ? formatUSCurrency(od.amount) : null;
  const closingDate = od.closingDate ? DateFormatter(od.closingDate) : null;
  const chips = [
    od.stage && { icon: 'target', label: od.stage },
    od.account && { icon: 'building', label: od.account },
    od.projectType && { icon: 'deal', label: od.projectType },
    od.leadSource && { icon: 'campaigns', label: od.leadSource },
  ].filter(Boolean);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'grid' },
    { id: 'activities', label: 'Activities', icon: 'clock' },
    { id: 'notes', label: 'Notes', icon: 'note', count: od.notesList?.length },
    { id: 'attachments', label: 'Attachments', icon: 'paperclip', count: od.attachments?.length },
    { id: 'emails', label: 'Emails', icon: 'mail', count: od.emails?.length },
  ];

  const overview = (
    <div className="gos-lr-grid2">
      <InfoCard
        title="Deal Information"
        icon="deal"
        rows={[
          ['Opportunity Name', val(name)],
          ['Account', val(od.account)],
          ['Contact', val(od.contact)],
          ['Stage', val(od.stage)],
          ['Amount', val(amount)],
          ['Probability', od.probability != null ? `${od.probability}%` : null],
          ['Closing Date', val(closingDate)],
          ['Expected Revenue', val(od.expectedRevenue != null ? formatUSCurrency(od.expectedRevenue) : null)],
        ]}
      />
      <InfoCard
        title="Pipeline & Source"
        icon="target"
        rows={[
          ['Project Type', val(od.projectType)],
          ['Next Step', val(od.nextStep)],
          ['Forecast Category', val(od.forecastCategory)],
          ['Campaign Source', val(od.campaignSource)],
          ['Reach Out for Marketing', od.reachOutForMarketing === true ? 'Yes' : od.reachOutForMarketing === false ? 'No' : null],
        ]}
      />
      <InfoCard
        title="Ownership & Classification"
        icon="user"
        rows={[
          ['Owner', val(od.owner)],
          ['Industry', val(od.industry)],
          ['Business Type', val(od.businessType)],
          ['Lead Source', val(od.leadSource)],
          ['Source Name', val(od.sourceName)],
          ['Other Source Name', val(od.otherSourceName)],
          ['Line Of Business', val(od.lineOfBusiness)],
        ]}
      />
      <InfoCard
        title="Opportunity Details"
        icon="note"
        rows={[
          ['Description', val(od.description)],
          ['Technology Stack', val(od.technologyStack)],
        ]}
      />
    </div>
  );

  const detailsPanel = (
    <div className="gos-lr-card gos-lr-sticky">
      <div className="gos-lr-card-head">
        <div className="gos-lr-card-title">
          <Ic name="info" size={16} /> Details
        </div>
        <button type="button" className="gos-lr-iconbtn" onClick={handleOpenEditForm} title="Edit opportunity">
          <Ic name="edit" size={14} />
        </button>
      </div>
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Deal</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="deal" label="Name">{val(name)}</DetailRow>
          <DetailRow icon="target" label="Stage">{val(od.stage)}</DetailRow>
          <DetailRow icon="briefcase" label="Amount">{val(amount)}</DetailRow>
          <DetailRow icon="calendar" label="Closing Date">{val(closingDate)}</DetailRow>
          <DetailRow icon="user" label="Owner">{val(od.owner)}</DetailRow>
        </div>
      </div>
      <div className="gos-lr-divider" />
      <div className="gos-lr-sec">
        <div className="gos-lr-sec-title">Classification</div>
        <div className="gos-lr-card-rows">
          <DetailRow icon="building" label="Account">{val(od.account)}</DetailRow>
          <DetailRow icon="user" label="Contact">{val(od.contact)}</DetailRow>
          <DetailRow icon="briefcase" label="Industry">{val(od.industry)}</DetailRow>
          <DetailRow icon="campaigns" label="Lead Source">{val(od.leadSource)}</DetailRow>
          <DetailRow icon="note" label="Source Name">{val(od.sourceName)}</DetailRow>
        </div>
      </div>
    </div>
  );

  let tabBody;
  if (activeTab === 'overview') tabBody = overview;
  else if (activeTab === 'activities') tabBody = <Timeline localData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'notes') tabBody = <Notes leadLocalData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'attachments') tabBody = <Attachments localData={opportunityLocalData} module={'Opportunity'} />;
  else if (activeTab === 'emails') tabBody = <Email localData={opportunityLocalData} module={'Opportunity'} />;

  return (
    <>
      {loading && <Loader />}

      <div className="ViewLeadPageOuter gos-leadrec">
        {/* back row */}
        <div className="gos-lr-backrow">
          <button type="button" className="gos-lr-ghost-btn" onClick={() => navigate('/opportunity')}>
            <Ic name="chevL" size={15} /> Opportunities list
          </button>
          {od.id ? (
            <div className="gos-lr-leadid">
              Opportunity <span>{od.id}</span>
            </div>
          ) : null}
        </div>

        {/* header card */}
        <div className="gos-lr-card gos-lr-header">
          <div className="gos-lr-header-top">
            <div className="gos-lr-header-l">
              <span className="gos-lr-ava" style={{ background: gosAvaColor(name) }}>
                {gosInitials(name)}
              </span>
              <div className="gos-lr-header-meta">
                <div className="gos-lr-name">{name}</div>
                <div className="gos-lr-subline">
                  <Ic name="building" size={14} />
                  {[od.account, od.stage].filter(Boolean).join(' · ') || '—'}
                  {amount ? (
                    <>
                      <span className="gos-lr-dot">·</span>
                      <Ic name="deal" size={14} />
                      {amount}
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="gos-lr-actions">
              <button type="button" className="gos-lr-contact-btn" onClick={() => setEmailOpen(true)}>
                <Ic name="mail" size={16} /> Mail
              </button>
              <button
                type="button"
                className="gos-lr-iconbtn gos-lr-iconbtn-bordered"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="More actions"
              >
                <Ic name="dots" size={18} />
              </button>
              {menuOpen ? (
                <>
                  <div className="gos-lr-menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="gos-lr-menu">
                    <button type="button" className="gos-lr-menu-item" onClick={() => { setMenuOpen(false); handleOpenEditForm(); }}>
                      <Ic name="edit" size={15} /> Edit Opportunity
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          {chips.length ? (
            <div className="gos-lr-chips">
              {chips.map((c, i) => (
                <span className="gos-lr-chip" key={i}>
                  <Ic name={c.icon} size={14} /> {c.label}
                </span>
              ))}
            </div>
          ) : null}
          <OpportunityTagEditor opportunity={od} />
        </div>

        {/* tabs */}
        <div className="gos-lr-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={'gos-lr-tab' + (activeTab === t.id ? ' active' : '')}
              onClick={() => setActiveTab(t.id)}
            >
              <Ic name={t.icon} size={15} /> {t.label}
              {t.count != null ? <span className="gos-lr-count">{t.count}</span> : null}
            </button>
          ))}
        </div>

        {/* body */}
        {activeTab === 'overview' ? (
          overview
        ) : (
          <div className="gos-lr-2col">
            <div className="gos-lr-main">{tabBody}</div>
            {detailsPanel}
          </div>
        )}
      </div>

      {emailOpen && (
        <ComposeEmail
          localData={opportunityLocalData}
          module={'Opportunity'}
          handleClose={() => setEmailOpen(false)}
          handleChange={handleChange}
        />
      )}
    </>
  );
};

export default ViewDeal;
